/**
 * The single, testable answer to "which CMMD workspace does Forge web scope
 * to right now" (CMMD-Center/forge#3572).
 *
 * Every prior guessing site resolved this independently, and all of them
 * eventually reduced to `bootstrap.activeWorkspace ?? workspaces[0]` --
 * `activeWorkspace` is CMMD's `organizationMembers.isDefault` fallback, which
 * tracks join history, not the workspace the account is actually using. This
 * resolver refuses that guess once it would matter: an explicit pick always
 * wins, a single membership is never ambiguous, and two or more memberships
 * with no pick ask rather than default to whichever happens to be marked
 * `isDefault`. Deliberately does not consult `bootstrap.activeWorkspace` at
 * all -- see the PR description for why the literal "else activeWorkspace"
 * step this issue's design section describes cannot be reconciled with
 * "ask when ambiguous and no pick" once `activeWorkspace` is populated.
 */

export interface ForgeWorkspaceMembership {
  readonly id: number;
  readonly name: string;
}

export interface ResolveCurrentForgeWorkspaceInput {
  /** A previously confirmed pick, from `currentForgeWorkspaceStore`. */
  readonly storedWorkspaceId: number | null;
  /** Every workspace this account belongs to, from CMMD's bootstrap. */
  readonly workspaces: ReadonlyArray<ForgeWorkspaceMembership>;
}

export type ResolveCurrentForgeWorkspaceResult =
  | { readonly kind: "resolved"; readonly organizationId: number; readonly workspaceName: string }
  | { readonly kind: "ask"; readonly options: ReadonlyArray<ForgeWorkspaceMembership> }
  | { readonly kind: "none" };

export function resolveCurrentForgeWorkspace(
  input: ResolveCurrentForgeWorkspaceInput,
): ResolveCurrentForgeWorkspaceResult {
  const stored =
    input.storedWorkspaceId !== null
      ? input.workspaces.find((workspace) => workspace.id === input.storedWorkspaceId)
      : undefined;
  if (stored) {
    return { kind: "resolved", organizationId: stored.id, workspaceName: stored.name };
  }

  if (input.workspaces.length === 0) {
    return { kind: "none" };
  }

  if (input.workspaces.length === 1) {
    const only = input.workspaces[0]!;
    return { kind: "resolved", organizationId: only.id, workspaceName: only.name };
  }

  return { kind: "ask", options: input.workspaces };
}
